import React, { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import classnames from 'classnames';
import queryString from 'query-string';

import {
  FitnessApplicationSettingsInput,
  PageTotal,
} from '../../../types/__generated__';
import { usePageMeta, usePageOwner } from '../../hooks';
import useUpdateFitnessSettings from '../../hooks/fitness/useUpdateFitnessSettings';
import { ActivityTotals } from '../ActivityTotals/ActivityTotals';
import Button from '../Button/Button';
import { pausedFitnessSettings } from '../FitnessSettings/FitnessSettingsModal/defaultFitnessSettings';
import Modal from '../Modal/Modal';
import { childComponentSelector } from './childComponentSelector';
import { FitnessLogo } from './FitnessLogo';
import { useFitnessManagerContext } from './FitnessManagerContext';

import styles from './FitnessManager.scss';

interface Text {
  status: string;
  description: string;
}

export interface FitnessManagerLayoutProps {
  children: ReactNode;
  text: {
    paused: Text;
    connected: Text;
    disconnected: Text;
  };
  url: {
    compliance: string;
  };
}

export function FitnessManagerLayout({
  children,
  text,
  url,
}: FitnessManagerLayoutProps) {
  const { isPageOwner } = usePageOwner();
  const { t } = useTranslation('common', { useSuspense: false });
  const {
    status: { isConnected, isPaused, hasSettings },
    settings: { showTotaliser },
    totals,
    modalManager,
  } = useFitnessManagerContext();
  const { status, description } = useText({ text });
  useOpenSettingsIfConnected();
  useCreateEmptySettingsIfMissing();

  if (
    !isPageOwner &&
    (!showTotaliser || !hasTimelineAmounts(totals.timeline))
  ) {
    return null;
  }

  return (
    <div className={styles.fitnessManager}>
      <div className={styles.headingContainer}>
        <span>
          {childComponentSelector(children, FitnessLogo)}
          {isConnected && (
            <span
              className={classnames(styles.connectionStatus, 'jg-text--small', {
                [styles.paused]: isPaused,
              })}
            >
              {status}
            </span>
          )}
        </span>
      </div>
      <div className={styles.body}>
        {childComponentSelector(children, ActivityTotals)}
      </div>
      {isPageOwner && (
        <div className={styles.body}>
          <div
            className={classnames(
              styles.button,
              styles.settings,
              'jg-bdt jg-bd--very-light-grey',
            )}
          >
            <p className="jg-text">
              <b>{description}</b>
            </p>
            <a className={styles.complianceText} href={url.compliance}>
              {t('fitnessSection.strava.privacyComplianceStatement')}
            </a>
            {isConnected && hasSettings
              ? childComponentSelector(children, SettingsButton)
              : childComponentSelector(children, ConnectButton)}
          </div>
          {childComponentSelector(children, AddManualActivityButton)}
        </div>
      )}
      {Boolean(modalManager.modal) ? (
        <Modal
          onCloseRequest={modalManager.actions.onCloseModal}
          scrollIsolation={modalManager.status.hasCookieConsent}
        >
          {modalManager.modal}
        </Modal>
      ) : null}
    </div>
  );
}

function useText({ text }: Pick<FitnessManagerLayoutProps, 'text'>) {
  const { status } = useFitnessManagerContext();

  if (status.isPaused) return text.paused;

  if (status.isConnected) return text.connected;

  return text.disconnected;
}

interface ButtonProps {
  onClick: () => void;
  className?: string;
  text: string;
}

export function hasTimelineAmounts(timeline: PageTotal[]) {
  return (
    timeline.length !== 0 &&
    timeline.some(entry => entry.amounts.some(amount => amount.value > 0))
  );
}

export function ConnectButton({ onClick, className, text }: ButtonProps) {
  return (
    <Button type="button" onClick={onClick} fullWidth className={className}>
      {text}
    </Button>
  );
}

export function SettingsButton({
  className,
  text,
}: Omit<ButtonProps, 'onClick'>) {
  const { modalManager } = useFitnessManagerContext();

  function handleOpenModal() {
    modalManager.actions.onOpenModal('settings');
  }

  return (
    <Button
      type="button"
      onClick={handleOpenModal}
      fullWidth
      className={className}
    >
      {text}
    </Button>
  );
}

export function AddManualActivityButton({
  className,
  text,
}: Omit<ButtonProps, 'onClick'>) {
  const { modalManager } = useFitnessManagerContext();

  function handleOpenModal() {
    modalManager.actions.onOpenModal('addManualActivity');
  }

  return (
    <Button
      outlined
      type="button"
      onClick={handleOpenModal}
      fullWidth
      className={className}
    >
      {text}
    </Button>
  );
}

// Fitbit and Strava auth is handled via an express route. When the route
// redirects to a fundraising page this will open the settings menu
function useOpenSettingsIfConnected() {
  const { pageSlug } = usePageMeta();
  const {
    fitnessApplicationName,
    modalManager: {
      actions: { onOpenModal },
    },
  } = useFitnessManagerContext();

  useEffect(() => {
    const { fitnessSourceConnected } = queryString.parse(
      window.location.search,
    );

    if (fitnessSourceConnected !== fitnessApplicationName) return;

    window.history.replaceState({}, '', pageSlug);

    onOpenModal('settings');
  }, [pageSlug, onOpenModal, fitnessApplicationName]);
}

function useCreateEmptySettingsIfMissing() {
  const {
    status: { hasSettings, isConnected },
    page: { id: pageId },
    fitnessApplicationName,
  } = useFitnessManagerContext();
  const [updateFitnessSettings] = useUpdateFitnessSettings();

  useEffect(() => {
    if (!hasSettings && isConnected) {
      const initialSettings: FitnessApplicationSettingsInput = {
        pageId,
        name: fitnessApplicationName,
        ...pausedFitnessSettings,
      };

      updateFitnessSettings(initialSettings);
    }
  }, [
    hasSettings,
    isConnected,
    pageId,
    fitnessApplicationName,
    updateFitnessSettings,
  ]);
}
